// A presentation published to DataViz (a web app dashboard), framed by
// DashboardView or SharedDashboardView. Three jobs:
// - `?at=s/l` opens the deck there (the iframe's hash), and the param follows
//   the deck as the person moves, so the URL can be shared at a slide;
// - Ask is open to a signed-in viewer who may start chats; an anonymous
//   share-link viewer sees it disabled with the sign-in reason;
// - Ask opens "Start a new chat from here" (there is no chat to ask in).
// A page that is not a deck never says `ready`, so the in-page Ask never
// shows there. The share-link page turns the in-page Ask off (`deckAsk:
// false`): its round actions button already holds "Start a new chat from
// here", and the two did the same job (Adam, 4 Nov 2026). The toolbar's "Start a new chat from here" (`startChat`) is
// the host's own button: it works for any story board, a plain story page
// included, without a handshake, and copies the whole page (no slide).
import { useState, type RefObject } from 'react'
import useRouter from './useRouter'
import useAiAccess from './useAiAccess'
import usePresentationAsk, { dashboardAskState } from './usePresentationAsk'
import usePresentationFrame from '../agent/hooks/usePresentationFrame'
import { formatDeckAt, formatDeckAtParam, parseDeckAt } from '../agent/presentationFrame'

export interface UsePublishedPresentationOptions {
  frameRef: RefObject<HTMLIFrameElement | null>
  /** The dashboard's customer and id: the seed's source, named by the host. */
  customer: string
  dashboardId: string
  /** The published web app's job (WebappDashboardConfig.job). */
  job: string
  signedIn: boolean
  /** The dashboard's customer is one the viewer belongs to. */
  inCustomer: boolean
  /** The dashboard's name, for the start screen's chip. */
  name?: string
  /** Offer the deck's own Ask (default true). Off, the deck draws no Ask
   *  button and no "Ask about this figure", and its asks are ignored. */
  deckAsk?: boolean
}

export default function usePublishedPresentation({ frameRef, customer, dashboardId, job, signedIn, inCustomer, name, deckAsk = true }: UsePublishedPresentationOptions) {
  const router = useRouter()
  const aiAccess = useAiAccess(signedIn && inCustomer ? customer : '')
  const allowed = aiAccess.can('agent_sessions')
  const startRefusal = allowed ? undefined : aiAccess.refusalFor('agent_sessions')
  const ask = usePresentationAsk({ startRefusal })
  // The hash the frame opens on, read once from the URL: the param then
  // follows the deck, and the frame must not reload on every move.
  const [initialHash] = useState(() => {
    const at = parseDeckAt(router.params.at)
    return at ? formatDeckAt(at) : ''
  })

  const askState = dashboardAskState({
    signedIn,
    inCustomer,
    // A mock or an older hook without `loaded` counts as answered.
    aiAccessLoaded: aiAccess.loaded !== false,
    startRefusal,
  })

  const frame = usePresentationFrame({
    frameRef,
    askState: deckAsk ? askState : undefined,
    onAsk: (deckAsk) => ask.handleOpen({ ask: deckAsk, customer, source: { kind: 'dashboard', id: dashboardId }, job }),
    onLocation: (at) => {
      const next = formatDeckAtParam(at)
      if (router.params.at !== next) router.setParams({ at: next }, false, { history: 'replace' })
    },
  })

  // The toolbar button follows the same gate as Ask. Undefined askState:
  // AI access is still loading, so the button waits, disabled.
  const startChat = {
    pending: askState === undefined,
    reason: askState && !askState.enabled ? askState.reason : undefined,
    handleStart: () => {
      if (!askState?.enabled) return
      ask.handleStartChatFromPage({ customer, source: { kind: 'dashboard', id: dashboardId }, job, label: name || 'Story board' })
    },
  }

  // The page said `ready`, so it is a deck with its own bar at the bottom.
  const isDeck = !!frame?.deck

  return { initialHash, ask, startChat, isDeck }
}
